// PYODIDE PROVIDER
import {createContext, useEffect, useState} from 'react';
import {
  setPyodide,
  PyodideApi,
  PyodideUserDefinedEndHandler,
  PyodideUserDefinedImgHandler,
  PyodideUserDefinedStdinHandler,
  PyodideUserDefinedStdoutHandler,
  PyodideUserDefinedStderrHandler,
} from './PyodideRunner';
import {ProjectFileData, ProjectFileId} from 'projects/types';
import {GradingScripts} from 'assignments/models/unitTests/advancedConsole';
import {useProductionErrorLogger} from 'components/errors/utils';
import {showPyodideErrorReloadSwal} from 'components/pyodide/pyodideErrorReloadSwal';

// this is akin to a global variable state

interface PyodideContextType {
  isPyodideLoading: boolean;
  pyodideError: Error | null;
}

const PYODIDE_CONTEXT_DEFAULT_DATA: PyodideContextType = {
  isPyodideLoading: true,
  pyodideError: null,
};

export const PyodideContext = createContext<PyodideContextType>(PYODIDE_CONTEXT_DEFAULT_DATA);

export class PyodideClient {
  private pyapi: PyodideApi;

  constructor() {
    this.pyapi = new PyodideApi();
  }

  /**
   *
   * @param code The code to run
   * @param activeFile The file whose code will be run
   * @param stepMode Whether to run the code in step mode
   * @param uninterrupted The number of steps to run before escaping via `unthrow`
   * @param canvasId The id of the canvas element to use
   * @returns
   */
  async runCode(
    code: string,
    activeFile: ProjectFileData = {name: 'main.py', id: ''},
    stepMode: boolean = true,
    uninterrupted: number = 2000,
    canvasId: string = 'canvas',
  ) {
    return await this.pyapi.runPython(
      code,
      activeFile,
      stepMode,
      uninterrupted,
      0,
      canvasId,
    );
  }

  /**
   *
   * @param filesCode: The entire project's code (i.e. all files and their contents)
   * @param fileToTest: The name of the file to test
   * @param input: The input to test the code with (for console unit tests)
   * @param gradingScripts: The grading scripts to use (for advanced console unit tests)
   */
  async testCode(
    code: string,
    fileToTest: ProjectFileData,
    input: string[] = [],
    gradingScripts: GradingScripts | null = null,
  ) {
    return this.pyapi.test(code, input, fileToTest, gradingScripts);
  }

  setHandlers(
    endFunc: PyodideUserDefinedEndHandler,
    outputFunc: PyodideUserDefinedStdoutHandler = () => {},
    errorFunc: PyodideUserDefinedStderrHandler = async () => '',
    inputFunc: PyodideUserDefinedStdinHandler = async () => '',
    getImageUrl: PyodideUserDefinedImgHandler = () => '',
  ) {
    this.pyapi.setUserDefinedFuncs(
      endFunc,
      outputFunc,
      errorFunc,
      inputFunc,
      getImageUrl,
    );
  }

  isPyRunning() {
    return this.pyapi.getRunningFlag();
  }

  raisePyStopFlag() {
    this.pyapi.setRunningFlag();
  }

  getStepInfo() {
    return {
      list: self.stepInfo.frames,
      logs: self.stepInfo.stdout,
      stepListPyproxy: self.stepInfo.step_list_pyproxy,
    };
  }

  setKarelSleepTime(time) {
    self.karelInfo.sleepTime = time;
  }

  stepKarel(state) {
    if (self.karelInfo.client) {
      self.karelInfo.client.setKarelState(state);
    }
  }

  stepGraphics(state) {
    if (self.canvasInfo.client) {
      self.canvasInfo.client.redrawStep(state);
    }
  }

  async loadFiles(
    fileEntries: ProjectFileData[],
    filesCode: Record<ProjectFileId, {content: string}>,
  ) {
    return await this.pyapi.loadFiles(fileEntries, filesCode);
  }

  async loadFile(path, contents) {
    return await this.pyapi.loadFile(path, contents);
  }

  setKarelInfo(startState, setWorldState, sleepTime) {
    this.pyapi.setKarelInfo(startState, setWorldState, sleepTime);
  }

  async setReplSession(exitRepl) {
    return await this.pyapi.enterReplMode(exitRepl);
  }

  async handleReplCommand(command) {
    return await this.pyapi.interpreter(command);
  }

  async endRepl() {
    return await this.pyapi.endRepl();
  }
}

interface LoadCodeInPlacePyodideParams {
  setPyodideError: (error: Error) => void;
}

const loadCodeInPlacePyodide = async ({setPyodideError}: LoadCodeInPlacePyodideParams) => {
  console.log('Loading Pyodide');
  await setPyodide({setPyodideError});
};

export default function PyodideProvider({children}) {
  const [isPyodideLoading, setIsPyodideLoading] = useState(true);
  const [pyodideError, setPyodideError] = useState<Error | null>(null);
  const {logProductionErrorToFirestore} = useProductionErrorLogger();

  useEffect(() => {
    loadCodeInPlacePyodide({setPyodideError})
      .catch(async error => {
        console.error('Pyodide Provider: Error loading Pyodide', error);
        await logProductionErrorToFirestore(error, {
          code: 'Pyodide Loading Error',
        }).catch(async (error) => {
          console.error('Pyodide Provider: Error logging Pyodide error', error);
        });
        setPyodideError(error);
      })
      .finally(() => {
        setIsPyodideLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!pyodideError) return;
    showPyodideErrorReloadSwal();
  }, [pyodideError]);

  return (
    <PyodideContext.Provider
      value={{
        isPyodideLoading,
        pyodideError,
      }}
    >
      {children}
    </PyodideContext.Provider>
  );
}
